
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, CalendarIcon, RefreshCcw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ItineraryEvent } from '@/types/itinerary';
import TravelScheduleTable from './TravelScheduleTable';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useIsMobile } from '@/hooks/use-mobile';
import AddTransportationDialog from './AddTransportationDialog';

interface TravelScheduleProps {
  transportationEvents: ItineraryEvent[];
  isLoading: boolean;
  error: string | null;
  isAuthorized: boolean;
  onRefresh: () => void;
  onRetry: () => void;
  onAddTransportation?: () => void;
  forSelectedDate?: boolean;
}

const TravelSchedule: React.FC<TravelScheduleProps> = ({
  transportationEvents,
  isLoading,
  error,
  isAuthorized,
  onRefresh,
  onRetry,
  onAddTransportation,
  forSelectedDate
}) => {
  const isMobile = useIsMobile();
  
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex flex-col space-y-2 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center">
            <CalendarIcon className="h-5 w-5 mr-2 text-glowingTeal" />
            <CardTitle className="text-lg">Schedule</CardTitle>
          </div>
          {isAuthorized && (
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={onRefresh} className="text-xs">
                <RefreshCcw className="h-3.5 w-3.5 mr-1" />
                Refresh
              </Button>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-4">
          Showing all transportation for all trip members
        </p>
        
        {isLoading ? (
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-glowingTeal" />
          </div>
        ) : error ? (
          <div className="space-y-4">
            <Alert variant="destructive" className="bg-red-50 border-red-200">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="text-red-700">
                {error}
              </AlertDescription>
            </Alert>
            <div className="flex justify-center">
              <Button variant="outline" onClick={onRetry} className="flex items-center space-x-2">
                <RefreshCcw className="h-4 w-4" />
                <span>Retry</span>
              </Button>
            </div>
          </div>
        ) : (
          <TravelScheduleTable 
            transportationEvents={transportationEvents} 
            forSelectedDate={forSelectedDate} 
          />
        )}
        
        <div className="text-sm text-muted-foreground mt-4 text-center">
          Trip transportation schedule for all travelers
        </div>
      </CardContent>
    </Card>
  );
};

export default TravelSchedule;
